/**
 * Klenzy cart drawer controller (pairs with sections/kl-cart-drawer.liquid).
 *
 * Public API (window.KlCart) and document events:
 *   KlCart.open() / 'kl:cart:open'           – open the drawer
 *   KlCart.close()                            – close it
 *   KlCart.refresh({ open }) / 'kl:cart:refresh' (detail.open) – re-render from the server
 * Emits 'kl:cart:updated' (detail.cart = /cart.js payload) after every refresh.
 * Also reacts to Savor's standard cart events so any native add-to-cart opens this drawer.
 */
import { StandardEvents } from '@shopify/events';

const SECTION_ID = 'kl-cart-drawer';
const root = () => document.querySelector('[data-kl-cart-drawer]');
const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

let lastFocus = null;
let busy = false;

function isGiftLine(item) {
  return Boolean(item.properties && (item.properties._free_gift || item.properties._gift_with));
}

/**
 * Keys of free-gift lines whose paid product is no longer in the cart. A gift tagged
 * `_gift_with` belongs to that product id; older gift lines without the tag only need
 * some paid product in the cart.
 */
function orphanedGiftKeys(items) {
  const paidProductIds = new Set(items.filter((item) => !isGiftLine(item)).map((item) => String(item.product_id)));
  return items
    .filter(isGiftLine)
    .filter((item) => {
      const parent = item.properties._gift_with;
      return parent ? !paidProductIds.has(String(parent)) : paidProductIds.size === 0;
    })
    .map((item) => item.key);
}

async function fetchCart() {
  const res = await fetch(`${rootUrl}cart.js`, { credentials: 'same-origin' });
  return res.json();
}

function swapContent(html) {
  const drawer = root();
  if (!drawer || !html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const next = doc.querySelector('[data-kl-cart-content]');
  const current = drawer.querySelector('[data-kl-cart-content]');
  if (next && current) current.replaceWith(next);
}

async function announce(cart) {
  const data = cart || (await fetchCart());
  document.dispatchEvent(new CustomEvent('kl:cart:updated', { detail: { cart: data } }));
  return data;
}

async function refresh({ open: shouldOpen = false } = {}) {
  const res = await fetch(`${rootUrl}?sections=${SECTION_ID}`, { credentials: 'same-origin' });
  const json = await res.json();
  swapContent(json[SECTION_ID]);
  await announce();
  if (shouldOpen) open();
}

function open() {
  const drawer = root();
  if (!drawer || drawer.classList.contains('is-open')) return;
  lastFocus = document.activeElement;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('kl-scroll-lock');
  drawer.querySelector('.kl-cart__panel')?.focus({ preventScroll: true });
}

function close() {
  const drawer = root();
  if (!drawer || !drawer.classList.contains('is-open')) return;
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('kl-scroll-lock');
  if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

/**
 * Changes a line's quantity (gift lines render no controls). When the paid product a gift
 * came with leaves the cart, the gift is removed too (it only ships with that product).
 */
async function changeLine(key, quantity) {
  if (busy) return;
  busy = true;
  const drawer = root();
  drawer?.classList.add('is-busy');

  try {
    let cart = await postJSON(`${rootUrl}cart/change.js`, { id: key, quantity, sections: SECTION_ID });
    let sections = cart.sections;

    if (Array.isArray(cart.items)) {
      const giftKeys = orphanedGiftKeys(cart.items);
      if (giftKeys.length > 0) {
        const updates = {};
        for (const giftKey of giftKeys) updates[giftKey] = 0;
        cart = await postJSON(`${rootUrl}cart/update.js`, { updates, sections: SECTION_ID });
        sections = cart.sections;
      }
    }

    if (sections && sections[SECTION_ID]) {
      swapContent(sections[SECTION_ID]);
      await announce();
    } else {
      await refresh();
    }
  } catch (error) {
    console.error('[kl-cart] Cart update failed', error);
    await refresh().catch(() => {});
  } finally {
    busy = false;
    root()?.classList.remove('is-busy');
  }
}

function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('[data-kl-cart-close]')) {
    event.preventDefault();
    close();
    return;
  }

  const removeBtn = target.closest('[data-kl-remove]');
  if (removeBtn) {
    event.preventDefault();
    changeLine(removeBtn.getAttribute('data-kl-remove'), 0);
    return;
  }

  const qtyBtn = target.closest('[data-kl-qty]');
  if (qtyBtn && !qtyBtn.disabled) {
    event.preventDefault();
    const quantity = Math.max(0, parseInt(qtyBtn.getAttribute('data-kl-qty'), 10) || 0);
    changeLine(qtyBtn.getAttribute('data-key'), quantity);
  }
}

let noteTimer;
function onInput(event) {
  const target = event.target;
  if (!(target instanceof HTMLTextAreaElement) || !target.hasAttribute('data-kl-cart-note')) return;
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    postJSON(`${rootUrl}cart/update.js`, { note: target.value }).catch(() => {});
  }, 400);
}

function init() {
  const drawer = root();
  if (!drawer || drawer.dataset.klReady) return;
  drawer.dataset.klReady = 'true';

  drawer.addEventListener('click', onClick);
  drawer.addEventListener('input', onInput);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  document.addEventListener('kl:cart:open', () => open());
  document.addEventListener('kl:cart:refresh', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    refresh({ open: Boolean(detail.open) }).catch((error) => console.error('[kl-cart]', error));
  });

  // Native Savor add-to-cart flows (quick add, product cards) report through standard events.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const lineEvent = /** @type {any} */ (event);
    const opensDrawer = lineEvent.action === 'add';
    Promise.resolve(lineEvent.promise)
      .then((result) => {
        if (result && result.detail && result.detail.didError) return;
        return refresh({ open: opensDrawer });
      })
      .catch(() => {});
  });

  // Keep the header indicator in sync with the server cart on load.
  announce().catch(() => {});
}

window.KlCart = { open, close, refresh, changeLine };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
